import { format, formatDistanceToNow, isToday, isTomorrow, isYesterday, parseISO, startOfDay, endOfDay, addMinutes, differenceInMinutes, isBefore, isAfter } from 'date-fns'
import { ar, it, es, fr, de, enUS } from 'date-fns/locale'
import type { Locale } from 'date-fns'
import i18next from '../i18n'

const DATE_LOCALES: Record<string, Locale> = { ar, it, es, fr, de, en: enUS }

export function getDateLocale(): Locale {
  return DATE_LOCALES[i18next.language] ?? enUS
}

export function formatDate(dateStr: string): string {
  const d = parseISO(dateStr)
  if (isToday(d)) return 'Today'
  if (isTomorrow(d)) return 'Tomorrow'
  if (isYesterday(d)) return 'Yesterday'
  return format(d, 'EEE, MMM d')
}

export function formatTime(timeStr: string): string {
  return timeStr
}

export function formatDateTime(dateTimeStr: string): string {
  return format(parseISO(dateTimeStr), 'MMM d, yyyy HH:mm')
}

export function formatRelative(dateTimeStr: string): string {
  return formatDistanceToNow(parseISO(dateTimeStr), { addSuffix: true })
}

export function todayISO(): string {
  return format(new Date(), 'yyyy-MM-dd')
}

export function todayStart(): Date {
  return startOfDay(new Date())
}

export function todayEnd(): Date {
  return endOfDay(new Date())
}

export function isWithinMinutes(dateTimeStr: string, minutes: number): boolean {
  const target = parseISO(dateTimeStr)
  const now = new Date()
  const diff = differenceInMinutes(target, now)
  return diff >= 0 && diff <= minutes
}

export function getTimeSlots(startHour: number, endHour: number, intervalMinutes: number): string[] {
  const slots: string[] = []
  let current = new Date()
  current.setHours(startHour, 0, 0, 0)
  const end = new Date()
  end.setHours(endHour, 0, 0, 0)

  while (isBefore(current, end)) {
    slots.push(format(current, 'HH:mm'))
    current = addMinutes(current, intervalMinutes)
  }
  return slots
}

export function getDurationProgress(dateTimeStr: string, durationMinutes: number): number {
  const start = parseISO(dateTimeStr)
  const now = new Date()
  if (isBefore(now, start)) return 0
  const end = addMinutes(start, durationMinutes)
  if (isAfter(now, end)) return 100
  const elapsed = differenceInMinutes(now, start)
  return Math.round((elapsed / durationMinutes) * 100)
}

export { format, parseISO, isToday, addMinutes }
